import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import SEO from "@/components/SEO";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const empty = {
    name: "",
    division: "",
    zilla: "",
    address: "",
    phone: "",
};

export default function AdminLibraries() {
    const [items, setItems] = useState([]);
    const [locations, setLocations] = useState({});
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [filterZilla, setFilterZilla] = useState("");

    // ✅ stable loader
    const load = useCallback(async () => {
        try {
            const url = filterZilla
                ? `/libraries?zilla=${encodeURIComponent(filterZilla)}`
                : "/libraries";

            const res = await api.get(url);
            setItems(res.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, [filterZilla]);

    // load locations
    useEffect(() => {
        api.get("/locations")
            .then((r) => setLocations(r.data || {}))
            .catch(() => {});
    }, []);

    // reload data
    useEffect(() => {
        load();
    }, [load]);

    const save = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                name: form.name,
                division: form.division,
                zilla: form.zilla,
                address: form.address,
                phone: form.phone,
            };

            if (editId) {
                await api.put(`/admin/libraries/${editId}`, payload);
                toast.success("Updated");
            } else {
                await api.post("/admin/libraries", payload);
                toast.success("Created");
            }

            setOpen(false);
            setForm(empty);
            setEditId(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this library?")) return;

        try {
            await api.delete(`/admin/libraries/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const allZillas = Object.values(locations || {}).flat();

    return (
        <div>
            <SEO title="Admin - Libraries" />
            {/* HEADER */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-bold text-brand-navy">
                    Libraries
                </h1>

                <div className="flex items-center gap-2">
                    <Select
                        value={filterZilla || "__all"}
                        onValueChange={(v) =>
                            setFilterZilla(v === "__all" ? "" : v)
                        }
                    >
                        <SelectTrigger className="w-44 h-9 text-sm">
                            <SelectValue placeholder="Filter by Zilla" />
                        </SelectTrigger>

                        <SelectContent className="z-[110]">
                            <SelectItem value="__all">All Zillas</SelectItem>
                            {allZillas.map((z) => (
                                <SelectItem key={z} value={z}>
                                    {z}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <button
                        onClick={() => {
                            setForm(empty);
                            setEditId(null);
                            setOpen(true);
                        }}
                        className="btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 font-semibold"
                    >
                        <Plus className="w-4 h-4" />
                        Add Library
                    </button>
                </div>
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-2xl border overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Division</th>
                            <th className="text-left px-4 py-3">Zilla</th>
                            <th className="text-left px-4 py-3">Address</th>
                            <th className="text-left px-4 py-3">Phone</th>
                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        {items.map((l) => (
                            <tr key={l.id} className="border-t">
                                <td className="px-4 py-3 font-medium">
                                    {l.name}
                                </td>
                                <td className="px-4 py-3">{l.division}</td>
                                <td className="px-4 py-3">{l.zilla}</td>
                                <td className="px-4 py-3 truncate max-w-[120px] sm:max-w-xs">
                                    {l.address}
                                </td>
                                <td className="px-4 py-3">{l.phone}</td>

                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => {
                                            setForm(l);
                                            setEditId(l.id);
                                            setOpen(true);
                                        }}
                                        className="p-1"
                                    >
                                        <Edit size={14} />
                                    </button>

                                    <button
                                        onClick={() => del(l.id)}
                                        className="p-1 text-red-500 ml-2"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="text-center py-8 text-gray-500 text-sm"
                                >
                                    No libraries found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* MODAL */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="w-[95vw] sm:w-full z-[100]">
                    <DialogHeader>
                        <DialogTitle>
                            {editId ? "Edit" : "Add"} Library
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={save} className="space-y-3">
                        <Input
                            label="Name"
                            value={form.name}
                            onChange={(v) =>
                                setForm({ ...form, name: v })
                            }
                        />

                        <div>
                            <label className="text-xs font-medium">Division</label>
                            <Select
                                value={form.division}
                                onValueChange={(v) =>
                                    setForm({ ...form, division: v, zilla: "" })
                                }
                            >
                                <SelectTrigger className="w-full h-9 text-sm mt-1">
                                    <SelectValue placeholder="Select Division" />
                                </SelectTrigger>
                                <SelectContent className="z-[110]">
                                    {Object.keys(locations).map((d) => (
                                        <SelectItem key={d} value={d}>{d}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-xs font-medium">Zilla</label>
                            <Select
                                value={form.zilla}
                                onValueChange={(v) =>
                                    setForm({ ...form, zilla: v })
                                }
                                disabled={!form.division}
                            >
                                <SelectTrigger className="w-full h-9 text-sm mt-1">
                                    <SelectValue placeholder="Select Zilla" />
                                </SelectTrigger>
                                <SelectContent className="z-[110]">
                                    {(locations[form.division] || []).map((z) => (
                                        <SelectItem key={z} value={z}>{z}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <Input
                            label="Address"
                            value={form.address}
                            onChange={(v) =>
                                setForm({ ...form, address: v })
                            }
                        />

                        <Input
                            label="Phone"
                            value={form.phone}
                            onChange={(v) =>
                                setForm({ ...form, phone: v })
                            }
                        />

                        <button className="btn-primary w-full py-2">
                            Save
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* INPUT */
function Input({ label, value, onChange }) {
    return (
        <div>
            <label className="text-xs font-medium">{label}</label>
            <input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
            />
        </div>
    );
}